import {keyBy, map, partial} from 'lodash';
import * as actions from "modules/actions";
import {EnumOrders, EnumTriviaLadders} from "modules/utils/enums";
import {createReducer} from 'redux-act';


export interface IQueryParams {
	limit: number,
	offset: number,
}

export interface ICommonOptions {
	ladder_type: EnumTriviaLadders,
	options: Partial<ILadderAPIOptions>
}

export interface ILadderAPIOptions extends IQueryParams {
	contest_id?: number,
	region?: string
	store_id?: number
	state_id?: string,
	user_stats?: number,
	month?: string,
	year?: string
	direction?: EnumOrders,
	limit: number,
	offset: number
}

export interface IMonthLadderAPIOptions extends IQueryParams {
	region?: string
	month?: string
	state_id?: string
	store_id?: number
}

export interface IRoundAPIOptions extends IQueryParams {
	round_id: number
	store_id?: number
	state_id?: string
}

export interface ITriviaLadderRound {
	"id": number,
	"points": number,
	"rank": number,
	"number_played_questions": number,
	"number_correct_answers": number,
	"number_questions": number,
	"is_fully_played": boolean,
	"is_perfect": boolean
}

export interface ITriviaLadder {
	"user_id": number,
	"team_id": number,
	"points": number,
	"max_contest_points": number,
	"rank": number,
	"is_own": boolean
	"check_in_type": string,
	"store_id": number,
	"state_id": string,
	"number_played_rounds": number,
	"number_perfect_rounds": number,
	"number_played_contests"?: number,
	"number_correct_answers": number,
	"name": string,
	rounds?: ITriviaLadderRound[]
}

export interface ILadderWithPodium {
	items: ITriviaLadder[],
	podium: ITriviaLadder[],
	next: boolean
	is_fetched?: boolean
}

export interface ITriviaLadderState {
	is_data_requested?: boolean,
	ladder: ITriviaLadder[],
	podium: ITriviaLadder[],
	is_show_ladder_modal: boolean,
	ladders: Record<EnumTriviaLadders, ILadderWithPodium>,
}

const defaultLadder: ILadderWithPodium = {
	podium: [],
	items: [],
	next: false,
	is_fetched: false
};

const defaultState: ITriviaLadderState = {
	ladder: [],
	podium: [],
	is_data_requested: false,
	is_show_ladder_modal: false,
	ladders: {
		[ EnumTriviaLadders.contest ]: defaultLadder,
		[ EnumTriviaLadders.contest_by_state ]: defaultLadder,
		[ EnumTriviaLadders.contest_by_store ]: defaultLadder,
		[ EnumTriviaLadders.month ]: defaultLadder,
		[ EnumTriviaLadders.month_by_state ]: defaultLadder,
		[ EnumTriviaLadders.month_by_store ]: defaultLadder,
		[ EnumTriviaLadders.round ]: defaultLadder,
		[ EnumTriviaLadders.round_by_state ]: defaultLadder,
		[ EnumTriviaLadders.round_by_store ]: defaultLadder,
		[ EnumTriviaLadders.overall ]: defaultLadder,
		[ EnumTriviaLadders.overall_by_state ]: defaultLadder,
		[ EnumTriviaLadders.overall_by_store ]: defaultLadder,

		[ EnumTriviaLadders.team_season ]: defaultLadder,
		[ EnumTriviaLadders.team_season_state ]: defaultLadder,
		[ EnumTriviaLadders.team_season_store ]: defaultLadder,

		[ EnumTriviaLadders.team_month ]: defaultLadder,
		[ EnumTriviaLadders.team_month_by_state ]: defaultLadder,
		[ EnumTriviaLadders.team_month_by_store ]: defaultLadder,

		[ EnumTriviaLadders.team_contest ]: defaultLadder,
		[ EnumTriviaLadders.team_contest_by_state ]: defaultLadder,
		[ EnumTriviaLadders.team_contest_by_store ]: defaultLadder,

		[ EnumTriviaLadders.team_round ]: defaultLadder,
		[ EnumTriviaLadders.team_round_by_store ]: defaultLadder,
		[ EnumTriviaLadders.team_round_by_state ]: defaultLadder,
	},
};

const fetchSuccess = (type: EnumTriviaLadders, state: ITriviaLadderState, payload: ILadderWithPodium): ITriviaLadderState => {

	return {
		...state,
		ladders: {
			...state.ladders,
			[ type ]: {
				...payload,
				items: [
					...state.ladders[ type ].items,
					...( payload?.items || [] ),
				],
				is_fetched: true
			}
		}
	}
};

const fetchFailed = (type: EnumTriviaLadders, state: ITriviaLadderState): ITriviaLadderState => {
	return {
		...state,
		ladders: {
			...state.ladders,
			[ type ]: {
				...defaultLadder,
				is_fetched: true
			}
		}
	}
};

const clearLadder = (state: ITriviaLadderState, ladder_type: EnumTriviaLadders) => {
	return {
		...state,
		ladder: [],
		ladders: {
			...state.ladders,
			[ ladder_type ]: {
				...state.ladders[ ladder_type ],
				is_fetched: false,
				items: []
			}
		},
		offset: 0
	}
};

const setFetchedFailedStatus = (type: EnumTriviaLadders) => {
	return {
		[ type ]: {
			...defaultLadder,
			is_fetched: true
		}
	};
};

export const trivia_ladders = createReducer<ITriviaLadderState>({}, defaultState)
	.on(actions.fetchLadderPending, (state) => ( {
		...state,
		is_data_requested: true
	} ))

	/**
	 * Overall by region
	 */
	.on(actions.fetchOverallLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.overall))
	.on(actions.fetchOverallLadderFailed, partial(fetchFailed, EnumTriviaLadders.overall))
	.on(actions.fetchOverallByStateLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.overall_by_state))
	.on(actions.fetchOverallByStateLadderFailed, partial(fetchFailed, EnumTriviaLadders.overall_by_state))
	.on(actions.fetchOverallByStoreLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.overall_by_store))
	.on(actions.fetchOverallByStoreLadderFailed, partial(fetchFailed, EnumTriviaLadders.overall_by_store))

	/**
	 * By Contest
	 */
	.on(actions.fetchContestLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.contest))
	.on(actions.fetchContestLadderFailed, partial(fetchFailed, EnumTriviaLadders.contest))
	.on(actions.fetchContestByStoreLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.contest_by_store))
	.on(actions.fetchContestByStoreLadderFailed, partial(fetchFailed, EnumTriviaLadders.contest_by_store))
	.on(actions.fetchContestByStateLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.contest_by_state))
	.on(actions.fetchContestByStateLadderFailed, partial(fetchFailed, EnumTriviaLadders.contest_by_state))

	/**
	 * By Month
	 */
	.on(actions.fetchMonthLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.month))
	.on(actions.fetchMonthLadderFailed, partial(fetchFailed, EnumTriviaLadders.month))
	.on(actions.fetchMonthByStoreLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.month_by_store))
	.on(actions.fetchMonthByStoreLadderFailed, partial(fetchFailed, EnumTriviaLadders.month_by_store))
	.on(actions.fetchMonthByStateLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.month_by_state))
	.on(actions.fetchMonthByStateLadderFailed, partial(fetchFailed, EnumTriviaLadders.month_by_state))

	/**
	 * By Round
	 */
	.on(actions.fetchRoundLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.round))
	.on(actions.fetchRoundLadderFailed, partial(fetchFailed, EnumTriviaLadders.round))
	.on(actions.fetchRoundStoreLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.round_by_store))
	.on(actions.fetchRoundStoreLadderFailed, partial(fetchFailed, EnumTriviaLadders.round_by_store))
	.on(actions.fetchRoundStateLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.round_by_state))
	.on(actions.fetchRoundStateLadderFailed, partial(fetchFailed, EnumTriviaLadders.round_by_state))

	/**
	 * Team
	 */
	.on(actions.fetchTeamContestLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.team_contest))
	.on(actions.fetchTeamContestLadderFailed, partial(fetchFailed, EnumTriviaLadders.team_contest))
	.on(actions.fetchTeamContestStoreLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.team_contest_by_store))
	.on(actions.fetchTeamContestStoreLadderFailed, partial(fetchFailed, EnumTriviaLadders.team_contest_by_store))
	.on(actions.fetchTeamContestStateLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.team_contest_by_state))
	.on(actions.fetchTeamContestStateLadderFailed, partial(fetchFailed, EnumTriviaLadders.team_contest_by_state))
	.on(actions.fetchTeamMonthLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.team_month))
	.on(actions.fetchTeamMonthLadderFailed, partial(fetchFailed, EnumTriviaLadders.team_month))
	.on(actions.fetchTeamMonthByStoreLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.team_month_by_store))
	.on(actions.fetchTeamMonthByStoreLadderFailed, partial(fetchFailed, EnumTriviaLadders.team_month_by_store))
	.on(actions.fetchTeamMonthByStateLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.team_month_by_state))
	.on(actions.fetchTeamMonthByStateLadderFailed, partial(fetchFailed, EnumTriviaLadders.team_month_by_state))
	.on(actions.fetchTeamSeasonLadderSuccess, partial(fetchSuccess, EnumTriviaLadders.team_season))
	.on(actions.fetchTeamSeasonLadderFailed, partial(fetchFailed, EnumTriviaLadders.team_season))
	/**
	 * Common
	 */
	.on(actions.fetchCommonLadderSuccess, (state, payload) => ( {
		...state,
		ladder: [],
		ladders: {
			...state.ladders,
			...payload
		},
		offset: 0
	} ))
	// eslint-disable-next-line sonarjs/no-identical-functions
	.on(actions.fetchTeamCommonLadderSuccess, (state, payload) => ( {
		...state,
		ladder: [],
		ladders: {
			...state.ladders,
			...payload
		},
		offset: 0
	} ))
	.on(actions.fetchCommonLadderFailed, (state) => ( {
		...state,
		ladder: [],
		ladders: {
			...state.ladders,
			...setFetchedFailedStatus(EnumTriviaLadders.contest),
			...setFetchedFailedStatus(EnumTriviaLadders.contest_by_store),
			...setFetchedFailedStatus(EnumTriviaLadders.contest_by_state),
			...setFetchedFailedStatus(EnumTriviaLadders.month),
			...setFetchedFailedStatus(EnumTriviaLadders.month_by_state),
			...setFetchedFailedStatus(EnumTriviaLadders.month_by_store),
		},
		offset: 0
	} ))
	.on(actions.fetchTeamCommonLadderFailed, (state) => ( {
		...state,
		ladder: [],
		ladders: {
			...state.ladders,
			...setFetchedFailedStatus(EnumTriviaLadders.team_contest),
			...setFetchedFailedStatus(EnumTriviaLadders.team_contest_by_state),
			...setFetchedFailedStatus(EnumTriviaLadders.team_contest_by_store),
			...setFetchedFailedStatus(EnumTriviaLadders.team_month),
			...setFetchedFailedStatus(EnumTriviaLadders.team_month_by_state),
			...setFetchedFailedStatus(EnumTriviaLadders.team_contest_by_store),
		},
		offset: 0
	} ))
	.on(actions.clearLadder, clearLadder)
	.on(actions.clearLadders, (state, payload) => {
		const ladders = map(payload, (type) => {
			return {
				type,
				...state.ladders[ type ],
				items: []
			}
		});

		const laddersByType = keyBy(ladders, 'type');

		return {
			...state,
			ladder: [],
			ladders: {
				...state.ladders,
				...laddersByType
			},
			offset: 0,
		}
	})

	.on(actions.clearAllLadders, (state, payload) => ( {
		...state,
		ladder: [],
		ladders: {
			...defaultState.ladders
		},
		offset: 0,
	} ))

	.on(actions.showLeaderboardModal, (state) => ( {
		...state,
		is_show_ladder_modal: true,
	} ))
	.on(actions.hideLeaderboardModal, (state) => ( {
		...state,
		is_show_ladder_modal: false,
	} ))

;
